import { GetMaterializedApprovalsFlowsDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  useBulkApproveMaterializedApprovalFlow,
  useBulkRejectMaterializedApprovalFlow,
} from 'modules/approvals/hooks/useMaterializedApprovalFlows'
import { APRROVER_STATUS } from 'modules/approvals/lib/roles'

type IBulkApproveRejectFlows = {
  accessId: number
  reason?: string
} & (
  | {
      materializedFlowId: number
    }
  | {
      materializedFlowIds: number[]
    }
)

const useBulkApproveRejectFlows = () => {
  const [fetchMaterializedApprovalsFlows] = useLazyQuery(GetMaterializedApprovalsFlowsDocument)
  const setFlashMessage = (message: string, type: 'correct' | 'wrong') => {
    app.setFlash({
      type,
      message,
      time: 2000,
    })
  }

  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()

  const { loading: approving, bulkApproveMaterializedFlow } =
    useBulkApproveMaterializedApprovalFlow()
  const { loading: rejecting, bulkRejectMaterializedFlow } = useBulkRejectMaterializedApprovalFlow()

  const fetchMultipleMaterializedApprovalFlows = async (materializedFlowIds: number[]) => {
    const { data, error } = await fetchMaterializedApprovalsFlows({
      query: GetMaterializedApprovalsFlowsDocument,
      variables: { ids: materializedFlowIds, companyId: currentCompanyId },
    })

    return {
      materializedApprovalsFlows: data?.approvals.materializedApprovalsFlowsConnection.nodes,
      error,
    }
  }

  const currentMaterializedApprovers = async (
    materializedFlowIds: number[],
    currentAccessId: number
  ) => {
    const { materializedApprovalsFlows } = await fetchMultipleMaterializedApprovalFlows(
      materializedFlowIds
    )

    return materializedFlowIds
      .map((flowId) => {
        const flow = materializedApprovalsFlows?.find((f) => f.id === flowId)

        if (!flow) return

        const flowApprover = flow.steps
          .flatMap((step) => step.stepApprovers)
          .find(
            (approver) =>
              approver.access?.id === currentAccessId && approver.status === APRROVER_STATUS.pending
          )

        if (!flowApprover) return

        return {
          materializedFlowId: flowId,
          approverId: flowApprover.id,
        }
      })
      .filter(Boolean)
  }

  const approve = async (
    actionData: IBulkApproveRejectFlows,
    onSuccess?: () => void,
    onError?: () => void,
    showSuccessMessage = true
  ) => {
    const approversAndFlows = await currentMaterializedApprovers(
      'materializedFlowId' in actionData
        ? [actionData.materializedFlowId]
        : actionData.materializedFlowIds,
      actionData.accessId
    )

    if (!approversAndFlows?.length) {
      onError?.()
      setFlashMessage(
        i18n.t('settings.approvals.approval_flow.flash_messages.error_update'),
        'wrong'
      )
      return
    }

    const variables = {
      updateWholeFlow: false,
      reason: actionData.reason,
      accessId: actionData.accessId,
      materializedStepApproverIds: approversAndFlows.map((it) => it.approverId),
      materializedApprovalFlowIds: approversAndFlows.map((it) => it.materializedFlowId),
      source: 'web',
    }

    const result = await bulkApproveMaterializedFlow(variables, currentCompanyId)
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      onSuccess?.()
      if (showSuccessMessage) {
        setFlashMessage(
          i18n.t('settings.approvals.approval_action.flash_messages.approve'),
          'correct'
        )
      }
    } else {
      onError?.()
      const error = handled.errors[0]
      const message =
        error && 'message' in error
          ? error.message
          : i18n.t('settings.approvals.approval_flow.flash_messages.error_update')
      setFlashMessage(message, 'wrong')
    }
  }

  const reject = async (
    actionData: IBulkApproveRejectFlows,
    onSuccess?: () => void,
    onError?: () => void,
    showSuccessMessage = true
  ) => {
    const approversAndFlows = await currentMaterializedApprovers(
      'materializedFlowId' in actionData
        ? [actionData.materializedFlowId]
        : actionData.materializedFlowIds,
      actionData.accessId
    )

    if (!approversAndFlows?.length) {
      onError?.()
      setFlashMessage(
        i18n.t('settings.approvals.approval_flow.flash_messages.error_update'),
        'wrong'
      )
      return
    }

    const variables = {
      updateWholeFlow: false,
      reason: actionData.reason,
      accessId: actionData.accessId,
      materializedStepApproverIds: approversAndFlows.map((it) => it.approverId),
      materializedApprovalFlowIds: approversAndFlows.map((it) => it.materializedFlowId),
      source: 'web',
    }

    const result = await bulkRejectMaterializedFlow(variables, currentCompanyId)
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      onSuccess?.()
      if (showSuccessMessage) {
        setFlashMessage(
          i18n.t('settings.approvals.approval_action.flash_messages.reject'),
          'correct'
        )
      }
    } else {
      onError?.()
      setFlashMessage(
        i18n.t('settings.approvals.approval_flow.flash_messages.error_update'),
        'wrong'
      )
    }
  }

  return {
    disabled: approving || rejecting,
    approve,
    reject,
  }
}

export default useBulkApproveRejectFlows
